import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useTrialExpirationHandler from './useTrialExpirationHandler';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface IdeaDraft {
  id: string;
  text: string; // API returns 'text', not 'content'
  draftNumber: number;
  projectedImpactLevel?: string;
  projectedImpactScore?: number;
  confidenceLevel?: string;
  rationale?: string;
  imageIdea?: string;
  brandPulseJson?: string;
  createdAt: string;
  
  // New fields directly from brandPulseMini response
  positivePct?: number;
  negativePct?: number;
  neutralPct?: number;
  score?: number;
  confidence?: string;
  replies1d?: number;
  replies7d?: number;
  likes7d?: number;
  shares7d?: number;
  saves7d?: number;
  driversPositive?: string[];
  delta?: string;
  recommendation?: string;
  reputation?: {
    repDelta: number;
    uiScore: number;
    level: string;
    color: string;
  };
}

export interface IdeaData {
  id: string;
  topic: string;
  brand: string;
  sector: string;
  platform: string;
  status?: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number | null;
  processingError?: string | null;
  audienceBaseline?: string; // New field from brandPulseMini
  icp: {
    id: string;
    title: string;
    data: string;
  };
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  drafts: IdeaDraft[];
  createdAt: string;
  isPrivate: boolean;
  relatedSimulationsByDraft?: Record<string, Array<{
    id: string;
    createdAt: string;
    platform?: string;
    isPrivate?: boolean;
    resultJson?: any;
    confidenceLevel?: string;
  }>>;
}

export interface UseIdeaResultsProps {
  ideaId: string | undefined;
  onError: (message: { type: 'success' | 'error' | 'warning' | 'info', message: string }) => void;
}

export interface UseIdeaResultsReturn {
  loading: boolean;
  ideaData: IdeaData | null;
  handleApiError: (error: any) => boolean;
  isExpiredModalOpen: boolean;
  isAdmin: boolean;
  closeExpiredModal: () => void;
  refreshData: () => void;
  setIdeaData: React.Dispatch<React.SetStateAction<IdeaData | null>>;
}

export const useIdeaResults = ({ ideaId, onError }: UseIdeaResultsProps): UseIdeaResultsReturn => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ideaData, setIdeaData] = useState<IdeaData | null>(null);

  // Trial expiration handling
  const { 
    isExpiredModalOpen, 
    isAdmin, 
    handleApiError, 
    closeExpiredModal 
  } = useTrialExpirationHandler();

  // Authentication check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch idea data
  const fetchIdeaData = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!ideaId || !isAuthenticated) {
      return;
    }

    let isMounted = true;
    const source = (axios as any)?.CancelToken?.source ? (axios as any).CancelToken.source() : { token: undefined, cancel: undefined };

    try {
      if (!silent) {
        setLoading(true);
      }
      
      // Auth handled via cookies with withCredentials
      const response = await axios.get(`${API_URL}/api/ideas/${ideaId}`, {
        withCredentials: true,
        cancelToken: source.token
      });
      
      if (!isMounted) return;
      
      setIdeaData(response.data.idea);
      // Attach related sims map if provided
      if (response.data.relatedSimulationsByDraft) {
        setIdeaData((prev) => prev ? { ...prev, relatedSimulationsByDraft: response.data.relatedSimulationsByDraft } : prev);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      
      if (!isMounted) return;
      
      if (!handleApiError(error)) {
        let errorMessage = 'Failed to load idea details';
        if (axios.isAxiosError(error)) {
          errorMessage = error.response?.data?.error || error.message || errorMessage;
        }
        onError({ type: 'error', message: errorMessage });
      }
    } finally {
      if (isMounted && !silent) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      if (source && typeof source.cancel === 'function') {
        source.cancel('Component unmounted or dependencies changed');
      }
    };
  };

  useEffect(() => {
    fetchIdeaData();
  }, [ideaId, isAuthenticated]); // initial load

  // Poll while idea is processing
  useEffect(() => {
    if (!ideaData) return;
    const needsPoll = ideaData.status === 'queued' || ideaData.status === 'processing' || (ideaData.drafts?.length === 0);
    if (!needsPoll) return;
    const interval = setInterval(() => {
      fetchIdeaData({ silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [ideaData?.status, ideaData?.id, ideaData?.drafts?.length]);

  return {
    loading,
    ideaData,
    handleApiError,
    isExpiredModalOpen,
    isAdmin,
    closeExpiredModal,
    refreshData: fetchIdeaData,
    setIdeaData
  };
};
